/**
 * Tenant-derived data builders
 * ============================================================================
 *
 * These functions produce the richer shapes that secondary pages
 * (Joint Assessments, Pay Bands, Compliance Reports) need, derived
 * deterministically from the core tenant data in tenants.js.
 *
 * Why derive instead of hard-code per tenant:
 *   - Single source of truth (tenants.js stays compact and reviewable)
 *   - Switching tenants regenerates everything coherently
 *   - When the CTO replaces this with real backend data, the derivation
 *     functions become the API client mappers, same shape, real source
 */

const ASSIGNEES = [
  { name: 'Sarah Chen', email: 'sarah.chen@clearequal.com' },
  { name: 'Michael Rodriguez', email: 'michael.rodriguez@clearequal.com' },
  { name: 'Emma Thompson', email: 'emma.thompson@clearequal.com' },
  { name: 'David Kim', email: 'david.kim@clearequal.com' },
  { name: 'Lisa Anderson', email: 'lisa.anderson@clearequal.com' },
  { name: 'Marco Bianchi', email: 'marco.bianchi@clearequal.com' },
];

/** Stable hash so same tenant+seed picks the same assignee */
const stableHash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const pickAssignee = (seed) => ASSIGNEES[stableHash(seed) % ASSIGNEES.length];

const priorityFromGap = (gap) => {
  if (gap >= 9) return 'critical';
  if (gap >= 7) return 'high';
  if (gap >= 5) return 'medium';
  return 'low';
};

/** Status progression based on alert status field */
const statusFromAlertState = (alertStatus) => {
  const map = {
    pending: 'open',
    in_progress: 'in-review',
    scheduled: 'open',
    completed: 'closed',
  };
  return map[alertStatus] || 'open';
};

/** Daily cost adjustment per affected worker per pp gap (rough estimate) */
const REMEDIATION_PER_WORKER_PER_PP = 3200;

/**
 * Build the detailed assessment list for a tenant.
 *
 * Strategy:
 *   1. Each existing `assessmentAlert` becomes a "live" assessment
 *      (currently in progress, open, or scheduled)
 *   2. Each category-of-workers row exceeding 5% gap gets a corresponding
 *      assessment record, historical or upcoming based on tenant compliance
 *   3. We add 2-3 closed historical assessments for context (so the table
 *      isn't suspiciously sparse)
 */
export function buildAssessments(tenant) {
  const out = [];
  let counter = 1;
  const idFor = (kind) => `JA-${tenant.reportingYear}-${kind}${String(counter++).padStart(3, '0')}`;

  // 1. Live assessments from alerts
  for (const alert of tenant.assessmentAlerts) {
    const seed = `${tenant.id}-${alert.id}`;
    const assignee = pickAssignee(seed);
    const affected = Math.max(
      8,
      Math.round(tenant.metrics.totalWorkers * 0.0015 * (alert.gapPercentage / 5))
    );
    const totalGap = Math.round(
      affected * alert.gapPercentage * REMEDIATION_PER_WORKER_PER_PP / 5
    );

    out.push({
      id: counter,
      caseId: alert.id.startsWith('ASS') ? `JA-${alert.id.slice(4)}` : idFor('L'),
      title: alert.title,
      description: alert.description,
      department: alert.department,
      gapPercentage: alert.gapPercentage,
      triggerDate: '2025-09-15',
      dueDate: alert.dueDate,
      assignee: assignee.name,
      assigneeEmail: assignee.email,
      priority: alert.priority === 'critical' ? 'critical' : alert.priority,
      status: statusFromAlertState(alert.status),
      affectedEmployees: affected,
      totalGapAmount: totalGap,
      directiveArticle: 'Article 10',
      isLive: true,
    });
  }

  // 2. Category-derived assessments, for each category exceeding 5%, add one
  //    if it's not already covered by the live alerts above
  const liveDepts = new Set(out.map((a) => a.department.toLowerCase()));
  for (const cat of tenant.categoriesOfWorkers) {
    if (cat.meanGap <= 5) continue;
    if (liveDepts.has(cat.name.toLowerCase())) continue;

    const seed = `${tenant.id}-cat-${cat.name}`;
    const assignee = pickAssignee(seed);
    const affected = Math.max(6, Math.round(cat.headcount * 0.018));
    const totalGap = Math.round(
      affected * cat.meanGap * REMEDIATION_PER_WORKER_PER_PP / 5
    );

    // Most are scheduled (future review); some closed (already resolved)
    const isResolved = stableHash(seed) % 3 === 0;

    out.push({
      id: counter,
      caseId: idFor(isResolved ? 'C' : 'S'),
      title: `${cat.name} pay equity review`,
      description: `Mean gap of ${cat.meanGap.toFixed(1)}% in ${cat.name} grade. ${
        isResolved
          ? 'Resolved through 2024 remediation cycle; documented for audit trail.'
          : 'Scheduled for the next compensation committee cycle.'
      }`,
      department: cat.name,
      gapPercentage: cat.meanGap,
      triggerDate: isResolved ? '2024-08-12' : '2025-10-20',
      dueDate: isResolved ? '2024-12-15' : '2026-03-31',
      assignee: assignee.name,
      assigneeEmail: assignee.email,
      priority: priorityFromGap(cat.meanGap),
      status: isResolved ? 'closed' : 'open',
      affectedEmployees: affected,
      totalGapAmount: totalGap,
      directiveArticle: 'Article 4',
      isLive: !isResolved,
    });
  }

  // 3. Add a couple of historical closed assessments for table density
  const historicalCount = Math.max(0, 3 - out.filter((a) => a.status === 'closed').length);
  const historicalCategories = tenant.categoriesOfWorkers.slice(0, historicalCount);

  for (const cat of historicalCategories) {
    const seed = `${tenant.id}-hist-${cat.name}`;
    const assignee = pickAssignee(seed);
    const histGap = +(cat.meanGap + 1.2).toFixed(1);
    const affected = Math.max(4, Math.round(cat.headcount * 0.012));
    const totalGap = Math.round(
      affected * histGap * REMEDIATION_PER_WORKER_PER_PP / 5
    );

    out.push({
      id: counter,
      caseId: idFor('H'),
      title: `${cat.name} historical review (closed)`,
      description: `Prior-period assessment closed with documented remediation. Final gap reduced from ${histGap.toFixed(1)}% to ${cat.meanGap.toFixed(1)}%.`,
      department: cat.name,
      gapPercentage: histGap,
      triggerDate: '2024-04-01',
      dueDate: '2024-09-30',
      assignee: assignee.name,
      assigneeEmail: assignee.email,
      priority: priorityFromGap(histGap),
      status: 'closed',
      affectedEmployees: affected,
      totalGapAmount: totalGap,
      directiveArticle: 'Article 4',
      isLive: false,
    });
  }

  return out;
}

/**
 * Aggregate stats for the assessments table header.
 */
export function buildAssessmentStats(assessments) {
  const total = assessments.length;
  const open = assessments.filter((a) => a.status === 'open' || a.status === 'in-review').length;
  const remediation = assessments.filter((a) => a.status === 'remediation').length;
  const closed = assessments.filter((a) => a.status === 'closed').length;
  const critical = assessments.filter((a) => a.priority === 'critical' && a.status !== 'closed').length;
  const totalAffected = assessments
    .filter((a) => a.status !== 'closed')
    .reduce((sum, a) => sum + (a.affectedEmployees || 0), 0);
  const totalGapEur = assessments
    .filter((a) => a.status !== 'closed')
    .reduce((sum, a) => sum + (a.totalGapAmount || 0), 0);

  return {
    total,
    open,
    inReview: assessments.filter((a) => a.status === 'in-review').length,
    remediation,
    closed,
    critical,
    totalAffected,
    totalGapEur,
  };
}
